/**
 * WorkspaceSplitView — Source reference and transform comparison views
 *
 * Extracted from MainWorkspace to reduce component size.
 * Two modes:
 * - "sources": Draft text with provenance highlighting + source cards
 * - "transform": Side-by-side original vs transformed content
 *
 * @module @humanizer/studio/components/workspace/WorkspaceSplitView
 */

import { MathMarkdown } from '../markdown';
import { PassageProvenanceView } from './PassageProvenanceView';
import type { SplitContent } from './MainWorkspace';

export interface SourceReferenceData {
  draftText: string;
  sourceCards: Array<{ title: string; excerpt: string }>;
  provenances?: Array<{
    type: 'verbatim' | 'paraphrase' | 'synthesis';
    sourceCardIndices: number[];
    startOffset: number;
    endOffset: number;
  }>;
}

export interface WorkspaceSplitViewProps {
  /** Which split sub-mode to render */
  mode: 'sources' | 'transform';
  /** Data for source reference view (mode === 'sources') */
  sourceReferenceView: SourceReferenceData | null;
  /** Data for transform comparison (mode === 'transform') */
  splitContent: SplitContent | null;
  /** Currently highlighted source card index */
  activeSourceIndex: number | null;
  /** Toggle a source card's active state */
  onActiveSourceIndexChange: (index: number | null) => void;
  /** Close the source reference view */
  onDismissSourceView: () => void;
}

export function WorkspaceSplitView({
  mode,
  sourceReferenceView,
  splitContent,
  activeSourceIndex,
  onActiveSourceIndexChange,
  onDismissSourceView,
}: WorkspaceSplitViewProps) {
  if (mode === 'sources' && sourceReferenceView) {
    return (
      <div className="workspace__split-view">
        <div className="workspace__split-pane">
          <div className="workspace__split-pane-header">Draft</div>
          <article className="workspace__article">
            {sourceReferenceView.provenances && sourceReferenceView.provenances.length > 0 ? (
              <PassageProvenanceView
                draftText={sourceReferenceView.draftText}
                provenances={sourceReferenceView.provenances}
                sourceCards={sourceReferenceView.sourceCards}
                onSourceClick={(cardIndex) => {
                  onActiveSourceIndexChange(activeSourceIndex === cardIndex ? null : cardIndex);
                }}
                activeSourceIndex={activeSourceIndex}
              />
            ) : (
              <MathMarkdown>{sourceReferenceView.draftText}</MathMarkdown>
            )}
          </article>
        </div>
        <div className="workspace__split-divider" />
        <div className="workspace__split-pane workspace__split-pane--sources">
          <div className="workspace__split-pane-header">
            Source Cards
            {activeSourceIndex !== null && (
              <button
                className="workspace__split-clear-filter"
                onClick={() => onActiveSourceIndexChange(null)}
                title="Show all sources"
              >
                Show all
              </button>
            )}
            <button
              className="workspace__split-close"
              onClick={onDismissSourceView}
              title="Close source view"
            >
              &times;
            </button>
          </div>
          <div className="workspace__source-cards">
            {sourceReferenceView.sourceCards.map((card, i) => (
              <div
                key={i}
                className={`workspace__source-card${activeSourceIndex === i ? ' workspace__source-card--active' : ''}`}
                onClick={() => onActiveSourceIndexChange(activeSourceIndex === i ? null : i)}
                ref={el => {
                  if (el && activeSourceIndex === i) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onActiveSourceIndexChange(activeSourceIndex === i ? null : i); }}
              >
                <div className="workspace__source-card-title">
                  {i + 1}. {card.title}
                </div>
                <div className="workspace__source-card-excerpt">{card.excerpt}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'transform' && splitContent) {
    return (
      <div className="workspace__split-view">
        <div className="workspace__split-pane">
          <div className="workspace__split-pane-header">{splitContent.originalLabel ?? 'Original'}</div>
          <article className="workspace__article">
            {splitContent.originalRenderMode === 'media' ? (
              splitContent.mediaType === 'image' ? (
                <img src={splitContent.original} alt="Media" className="workspace__split-media" />
              ) : splitContent.mediaType === 'audio' ? (
                <audio controls preload="metadata" src={splitContent.original} className="workspace__split-media" />
              ) : splitContent.mediaType === 'video' ? (
                <video controls preload="metadata" src={splitContent.original} className="workspace__split-media" />
              ) : (
                <MathMarkdown>{splitContent.original}</MathMarkdown>
              )
            ) : (
              <MathMarkdown>{splitContent.original}</MathMarkdown>
            )}
          </article>
        </div>
        <div className="workspace__split-divider" />
        <div className="workspace__split-pane">
          <div className="workspace__split-pane-header">{splitContent.transformedLabel ?? 'Transformed'}</div>
          <article className="workspace__article">
            <MathMarkdown>{splitContent.transformed}</MathMarkdown>
          </article>
        </div>
      </div>
    );
  }

  return null;
}
